import React, { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import {
    Alert,
    alpha,
    Box,
    Button,
    Chip,
    Container,
    FormControl,
    Grid,
    InputLabel,
    MenuItem,
    Paper,
    Select,
    Skeleton,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Typography,
} from "@mui/material";
import InsightsOutlinedIcon from "@mui/icons-material/InsightsOutlined";
import WarningAmberOutlinedIcon from "@mui/icons-material/WarningAmberOutlined";
import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined";
import PictureAsPdfOutlinedIcon from "@mui/icons-material/PictureAsPdfOutlined";
import {
    Bar,
    BarChart,
    CartesianGrid,
    Legend,
    Line,
    LineChart,
    ResponsiveContainer,
    Tooltip,
    XAxis,
    YAxis,
} from "recharts";
import {
    buildAnalyticsUrl,
    formatAnalyticsUnreachableMessage,
    isSupabaseFunctionsApi,
} from "../../config/api";
import { downloadAnalyticsCsv, downloadAnalyticsPdf, downloadAnalyticsFromApi } from "../../utils/analyticsExport";
import LibraryAnalyticsPanel from "../library/LibraryAnalyticsPanel";

const TERM_OPTIONS = ["Term 1", "Term 2", "Term 3"];

function KpiCard({ title, value, subtitle, color = "primary" }) {
    return (
        <Paper variant="outlined" sx={{ p: 2, height: "100%", borderRadius: 2 }}>
            <Typography variant="caption" color="text.secondary" fontWeight={700}>
                {title}
            </Typography>
            <Typography variant="h5" fontWeight={800} color={`${color}.main`} sx={{ mt: 0.5 }}>
                {value}
            </Typography>
            {subtitle ? (
                <Typography variant="caption" color="text.secondary">
                    {subtitle}
                </Typography>
            ) : null}
        </Paper>
    );
}

function riskChipColor(level) {
    if (level === "high") return "error";
    if (level === "medium") return "warning";
    return "default";
}

export default function AnalyticsDashboard({ mode = "admin", schoolId, teacherId, classOptions = [] }) {
    const now = new Date();
    const baseYear = now.getMonth() >= 8 ? now.getFullYear() : now.getFullYear() - 1;
    const academicYearOptions = useMemo(
        () => [`${baseYear - 1}/${baseYear}`, `${baseYear}/${baseYear + 1}`, `${baseYear + 1}/${baseYear + 2}`],
        [baseYear]
    );

    const [academicYear, setAcademicYear] = useState(academicYearOptions[1]);
    const [term, setTerm] = useState("");
    const [classId, setClassId] = useState("");
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");
    const [exporting, setExporting] = useState("");

    const load = useCallback(async () => {
        if (!schoolId || (mode === "teacher" && !teacherId)) return;
        setLoading(true);
        setError("");
        try {
            const params = new URLSearchParams({ schoolId, academicYear });
            if (term) params.set("term", term);
            if (classId) params.set("classId", classId);
            const path =
                mode === "teacher"
                    ? `/Analytics/teacher/${teacherId}?${params.toString()}`
                    : `/Analytics/${schoolId}?${params.toString()}`;
            const { data: payload } = await axios.get(buildAnalyticsUrl(path));
            setData(payload);
        } catch (err) {
            const apiMsg = err.response?.data?.message || err.response?.data?.error;
            let msg = apiMsg || (err.code === "ERR_NETWORK" ? formatAnalyticsUnreachableMessage() : null);
            if (!msg && err.response?.status === 404 && isSupabaseFunctionsApi()) {
                msg = "Analytics API is not deployed. Redeploy: supabase functions deploy FeeReports";
            }
            if (msg === "Id is required" && isSupabaseFunctionsApi()) {
                msg = "Analytics API needs redeploying. Run: supabase functions deploy FeeReports";
            }
            if (msg === "Unknown library route") {
                msg = "Analytics API needs redeploying. Run: supabase functions deploy FeeReports";
            }
            setError(msg || err.message || "Could not load analytics.");
            setData(null);
        } finally {
            setLoading(false);
        }
    }, [mode, schoolId, teacherId, academicYear, term, classId]);

    useEffect(() => {
        load();
    }, [load]);

    const filteredClassOptions = useMemo(() => {
        if (mode !== "teacher" || !Array.isArray(data?.teacherClasses)) return classOptions;
        const allowed = new Set(data.teacherClasses);
        return classOptions.filter((item) => allowed.has(item.id || item._id));
    }, [mode, data, classOptions]);

    const handleExport = async (format, report) => {
        setExporting(`${format}-${report}`);
        try {
            if (mode === "admin") {
                await downloadAnalyticsFromApi({ schoolId, academicYear, term, classId, format, report });
            } else if (data) {
                if (format === "pdf") downloadAnalyticsPdf(data, "Teacher analytics report");
                else downloadAnalyticsCsv(data, report);
            }
        } catch (err) {
            setError(err.message || "Export failed.");
        } finally {
            setExporting("");
        }
    };

    const summary = data?.summary || {};

    return (
        <Box sx={{ py: { xs: 2, md: 3 } }}>
            <Container maxWidth="xl">
                <Stack spacing={2} sx={{ mb: 3 }}>
                    <Stack direction="row" spacing={1.5} alignItems="center">
                        <InsightsOutlinedIcon color="primary" sx={{ fontSize: 32 }} />
                        <Box>
                            <Typography variant="h5" fontWeight={800}>
                                {mode === "teacher" ? "Class analytics" : "Advanced analytics"}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                                {mode === "teacher"
                                    ? "Performance trends, attendance, fee collection, and at-risk student indicators."
                                    : "Performance trends, attendance, fees, at-risk students, and library stock & loans."}
                            </Typography>
                        </Box>
                    </Stack>

                    <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
                        <Grid container spacing={2} alignItems="center">
                            <Grid item xs={12} sm={6} md={3}>
                                <FormControl fullWidth size="small">
                                    <InputLabel>Academic year</InputLabel>
                                    <Select label="Academic year" value={academicYear} onChange={(e) => setAcademicYear(e.target.value)}>
                                        {academicYearOptions.map((year) => (
                                            <MenuItem key={year} value={year}>
                                                {year}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                            </Grid>
                            <Grid item xs={12} sm={6} md={3}>
                                <FormControl fullWidth size="small">
                                    <InputLabel>Term</InputLabel>
                                    <Select label="Term" value={term} onChange={(e) => setTerm(e.target.value)}>
                                        <MenuItem value="">All terms</MenuItem>
                                        {TERM_OPTIONS.map((item) => (
                                            <MenuItem key={item} value={item}>
                                                {item}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                            </Grid>
                            <Grid item xs={12} sm={6} md={3}>
                                <FormControl fullWidth size="small">
                                    <InputLabel>Class</InputLabel>
                                    <Select label="Class" value={classId} onChange={(e) => setClassId(e.target.value)}>
                                        <MenuItem value="">All classes</MenuItem>
                                        {filteredClassOptions.map((item) => (
                                            <MenuItem key={item.id || item._id} value={item.id || item._id}>
                                                {item.sclassName}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                            </Grid>
                            <Grid item xs={12} sm={6} md={3}>
                                <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                                    <Button size="small" variant="outlined" startIcon={<FileDownloadOutlinedIcon />} disabled={!!exporting} onClick={() => handleExport("csv", "summary")}>
                                        Excel
                                    </Button>
                                    <Button size="small" variant="outlined" startIcon={<FileDownloadOutlinedIcon />} disabled={!!exporting} onClick={() => handleExport("csv", "atrisk")}>
                                        At-risk CSV
                                    </Button>
                                    <Button size="small" variant="outlined" startIcon={<PictureAsPdfOutlinedIcon />} disabled={!!exporting} onClick={() => handleExport("pdf", "summary")}>
                                        PDF
                                    </Button>
                                </Stack>
                            </Grid>
                        </Grid>
                    </Paper>
                </Stack>

                {error ? (
                    <Alert severity="warning" sx={{ mb: 2 }}>
                        {error}
                    </Alert>
                ) : null}

                {loading && !data ? (
                    <Skeleton variant="rounded" height={420} />
                ) : (
                    <>
                        <Grid container spacing={2} sx={{ mb: 2 }}>
                            <Grid item xs={6} md={2}>
                                <KpiCard title="Students" value={summary.studentCount ?? "—"} />
                            </Grid>
                            <Grid item xs={6} md={2}>
                                <KpiCard title="Attendance" value={summary.overallAttendance != null ? `${summary.overallAttendance}%` : "—"} color="success" />
                            </Grid>
                            <Grid item xs={6} md={2}>
                                <KpiCard title="Avg mark" value={summary.overallAverageMark ?? "—"} color="info" />
                            </Grid>
                            <Grid item xs={6} md={2}>
                                <KpiCard title="Fee collection" value={summary.feeCollectionRate != null ? `${summary.feeCollectionRate}%` : "—"} color="secondary" />
                            </Grid>
                            <Grid item xs={6} md={2}>
                                <KpiCard title="At-risk" value={summary.atRiskCount ?? "—"} color="warning" subtitle="Composite risk score" />
                            </Grid>
                            <Grid item xs={6} md={2}>
                                <KpiCard title="Dropout risk" value={summary.dropoutRiskCount ?? "—"} color="error" subtitle="Multiple warning signals" />
                            </Grid>
                        </Grid>

                        <Grid container spacing={2} sx={{ mb: 2 }}>
                            <Grid item xs={12} md={6}>
                                <Paper variant="outlined" sx={{ p: 2, borderRadius: 2, height: 320 }}>
                                    <Typography variant="subtitle1" fontWeight={700} gutterBottom>
                                        Performance trend
                                    </Typography>
                                    {(data?.performanceTrend || []).length ? (
                                        <ResponsiveContainer width="100%" height={250}>
                                            <LineChart data={data.performanceTrend}>
                                                <CartesianGrid strokeDasharray="3 3" />
                                                <XAxis dataKey="label" tick={{ fontSize: 11 }} />
                                                <YAxis domain={[0, 100]} />
                                                <Tooltip />
                                                <Legend />
                                                <Line type="monotone" dataKey="averageMark" name="Average mark" stroke="#1976d2" strokeWidth={2} />
                                                <Line type="monotone" dataKey="passRate" name="Pass rate %" stroke="#2e7d32" strokeWidth={2} />
                                            </LineChart>
                                        </ResponsiveContainer>
                                    ) : (
                                        <Typography variant="body2" color="text.secondary">
                                            No published marks for this filter yet.
                                        </Typography>
                                    )}
                                </Paper>
                            </Grid>
                            <Grid item xs={12} md={6}>
                                <Paper variant="outlined" sx={{ p: 2, borderRadius: 2, height: 320 }}>
                                    <Typography variant="subtitle1" fontWeight={700} gutterBottom>
                                        Attendance trend (weekly)
                                    </Typography>
                                    {(data?.attendanceTrend || []).length ? (
                                        <ResponsiveContainer width="100%" height={250}>
                                            <LineChart data={data.attendanceTrend}>
                                                <CartesianGrid strokeDasharray="3 3" />
                                                <XAxis dataKey="week" tick={{ fontSize: 11 }} />
                                                <YAxis domain={[0, 100]} />
                                                <Tooltip />
                                                <Line type="monotone" dataKey="attendanceRate" name="Attendance %" stroke="#ed6c02" strokeWidth={2} />
                                            </LineChart>
                                        </ResponsiveContainer>
                                    ) : (
                                        <Typography variant="body2" color="text.secondary">
                                            No attendance records for this filter yet.
                                        </Typography>
                                    )}
                                </Paper>
                            </Grid>
                            <Grid item xs={12} md={6}>
                                <Paper variant="outlined" sx={{ p: 2, borderRadius: 2, height: 320 }}>
                                    <Typography variant="subtitle1" fontWeight={700} gutterBottom>
                                        Subject performance
                                    </Typography>
                                    {(data?.subjectPerformance || []).length ? (
                                        <ResponsiveContainer width="100%" height={250}>
                                            <BarChart data={data.subjectPerformance.slice(0, 8)}>
                                                <CartesianGrid strokeDasharray="3 3" />
                                                <XAxis dataKey="subject" tick={{ fontSize: 10 }} interval={0} angle={-20} textAnchor="end" height={60} />
                                                <YAxis domain={[0, 100]} />
                                                <Tooltip />
                                                <Bar dataKey="averageMark" name="Average mark" fill="#1976d2" radius={[4, 4, 0, 0]} />
                                            </BarChart>
                                        </ResponsiveContainer>
                                    ) : (
                                        <Typography variant="body2" color="text.secondary">
                                            No subject breakdown available.
                                        </Typography>
                                    )}
                                </Paper>
                            </Grid>
                            <Grid item xs={12} md={6}>
                                <Paper variant="outlined" sx={{ p: 2, borderRadius: 2, height: 320 }}>
                                    <Typography variant="subtitle1" fontWeight={700} gutterBottom>
                                        Fee collection
                                    </Typography>
                                    <Stack spacing={1.5} sx={{ mt: 2 }}>
                                        <Typography variant="body2">
                                            Total due: <strong>{data?.feeCollection?.totalDue?.toLocaleString?.() ?? "—"}</strong>
                                        </Typography>
                                        <Typography variant="body2">
                                            Collected: <strong>{data?.feeCollection?.totalPaid?.toLocaleString?.() ?? "—"}</strong>
                                        </Typography>
                                        <Typography variant="body2">
                                            Outstanding: <strong>{data?.feeCollection?.totalBalance?.toLocaleString?.() ?? "—"}</strong>
                                        </Typography>
                                        <Typography variant="body2">
                                            Paid: {data?.feeCollection?.paidCount ?? 0} · Partial: {data?.feeCollection?.partialCount ?? 0} · Unpaid:{" "}
                                            {data?.feeCollection?.unpaidCount ?? 0}
                                        </Typography>
                                    </Stack>
                                </Paper>
                            </Grid>
                        </Grid>

                        <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
                            <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 2 }}>
                                <WarningAmberOutlinedIcon color="warning" />
                                <Typography variant="subtitle1" fontWeight={800}>
                                    At-risk students and dropout indicators
                                </Typography>
                            </Stack>
                            <TableContainer>
                                <Table size="small">
                                    <TableHead>
                                        <TableRow>
                                            <TableCell>Student</TableCell>
                                            <TableCell>Class</TableCell>
                                            <TableCell align="right">Risk</TableCell>
                                            <TableCell align="right">Attendance</TableCell>
                                            <TableCell align="right">Avg mark</TableCell>
                                            <TableCell align="right">Fee balance</TableCell>
                                            <TableCell>Dropout signals</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {(data?.atRiskStudents || []).length ? (
                                            data.atRiskStudents.map((row) => (
                                                <TableRow key={row.studentId} sx={{ bgcolor: row.dropoutRisk ? (t) => alpha(t.palette.error.main, 0.06) : undefined }}>
                                                    <TableCell>{row.name}</TableCell>
                                                    <TableCell>{row.className}</TableCell>
                                                    <TableCell align="right">
                                                        <Chip size="small" label={row.riskScore} color={riskChipColor(row.riskLevel)} />
                                                    </TableCell>
                                                    <TableCell align="right">{row.attendancePct}%</TableCell>
                                                    <TableCell align="right">{row.averageMark || "—"}</TableCell>
                                                    <TableCell align="right">{row.feeBalance?.toLocaleString?.() ?? row.feeBalance}</TableCell>
                                                    <TableCell>
                                                        {row.dropoutRisk ? (
                                                            <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
                                                                {(row.dropoutSignals || []).map((signal) => (
                                                                    <Chip key={signal} size="small" label={signal} color="error" variant="outlined" />
                                                                ))}
                                                            </Stack>
                                                        ) : (
                                                            <Typography variant="caption" color="text.secondary">
                                                                Monitor
                                                            </Typography>
                                                        )}
                                                    </TableCell>
                                                </TableRow>
                                            ))
                                        ) : (
                                            <TableRow>
                                                <TableCell colSpan={7}>
                                                    <Typography variant="body2" color="text.secondary">
                                                        No at-risk students flagged for the current filters.
                                                    </Typography>
                                                </TableCell>
                                            </TableRow>
                                        )}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        </Paper>
                    </>
                )}

                {mode === "admin" && schoolId ? <LibraryAnalyticsPanel schoolId={schoolId} /> : null}
            </Container>
        </Box>
    );
}
